import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth';
import { AlertService } from '../shared/services/alert.service';
import { FrontendDiagnosticsService } from './services/frontend-diagnostics.service';

//import { AccountService } from '../services/account.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private alertService: AlertService,
    private frontendDiagnosticsService: FrontendDiagnosticsService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(err => {
      // Extract error message
      const errorMessage = (err.error && err.error.message) ? err.error.message :
        (err.errors && err.errors.model && err.errors.model.length) ? err.errors.model[0] :
          (err.title) ? err.title :
            (err.error || err.statusText || err.message || 'Unknown error');

      // Check if this is a bad request error for GetPatientInfo with the specific message
      const isGetPatientInfoRequest = request.url.includes('/api/Staff/GetPatientInfo');
      const isBadRequest = err.status === 400;
      if (err.status === 401 || err.status === 403 || isBadRequest) {
        this.frontendDiagnosticsService.addDiagnostic('http-auth-related-error', JSON.stringify({
          status: err.status,
          url: request.url,
          method: request.method,
          message:
            typeof errorMessage === 'string'
              ? errorMessage
              : (errorMessage as any)?.Message || (errorMessage as any)?.message || '',
        }));
      }

      if (isGetPatientInfoRequest && isBadRequest) {
        return throwError(errorMessage.Message);
      }

      if (err.status === 401 && this.shouldRetryUnauthorizedRequest(request)) {
        return this.authService.refreshTokenOnce().pipe(
          switchMap((token) => {
            if (!token?.access_token) {
              throw err;
            }

            const retryRequest = request.clone({
              setHeaders: {
                Authorization: `Bearer ${token.access_token}`,
                clientname: this.authService.getClientFromUrl(),
              },
            });

            return next.handle(retryRequest);
          }),
          catchError((retryError) => {
            if (this.isSparkSyncRequest(request)) {
              console.warn(
                'Spark sync request failed after token refresh; keeping local data pending',
                retryError
              );
              return throwError(errorMessage);
            }

            console.error('Unauthorized request ' + request.url + ' signing out');
            this.authService.signOut().subscribe();
            return throwError(errorMessage);
          })
        );
      }

      // Check if this is a permission-related error for supervisor pages
      if ((err.status === 403) && 
          (this.authService.isSignedIn())) {
        // User is signed in but lacks permission for this patient - show error instead of dumping to selector
        console.error('Permission error for supervisor accessing patient', err);
        const permissionErrorMessage = 'You do not have permission to access. ' + errorMessage;
        this.alertService.error(permissionErrorMessage);
      } else if (err.status === 401) {
        if (this.isSparkSyncRequest(request)) {
          console.warn(
            'Spark sync request unauthorized; keeping local data pending'
          );
          return throwError(errorMessage);
        }

        // Regular unauthorized error handling - redirect to login
        console.error('Unauthorized request ' + request.url + ' signing out');
        this.authService.signOut().subscribe();
      }

      return throwError(errorMessage);
    }));
  }

  private shouldRetryUnauthorizedRequest(request: HttpRequest<any>): boolean {
    return (
      request.url.includes('/api/') &&
      !request.url.includes('/api/Account/RefreshToken') &&
      this.authService.hasUsableRefreshToken()
    );
  }

  private isSparkSyncRequest(request: HttpRequest<any>): boolean {
    return [
      '/api/Staff/SavePatientSession',
      '/api/Staff/SavePatientTargetData',
      '/api/Staff/SavePatientBehaviorData',
      '/api/Staff/SaveSessionProgressNotes',
      '/api/Staff/UploadGuardianSignature',
    ].some((url) => request.url.includes(url));
  }
}
